import i18n from '~i18n';
import {
    downloadText,
    formatGeneratedAt,
    formatRate,
    neverLoggedInFileName,
    reportFileName
} from './download';

const BOM = '\uFEFF';
const EOL = '\r\n';

/**
 * RFC 4180 field escaping: wrap in quotes when the value contains a
 * separator, quote, or line break; double any embedded quotes.
 * @param {*} value
 * @returns {string}
 */
export function csvField(value) {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // Neutralise spreadsheet formula injection (=, +, -, @ prefixes).
    const safe = /^[=+\-@\t\r]/.test(str) ? `'${str}` : str;
    return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * Build the CSV document for an activity report. Two sections: a totals
 * block (metric / value) followed by the daily breakdown table.
 * @param {object} report API response from /business/activity-report
 * @returns {string}
 */
export function buildCsv(report) {
    const t = key => i18n.t(`activityReport.${key}`);
    const { totals } = report;
    const meditatedRate = totals.uniqueLogins
        ? totals.uniqueMeditated / totals.uniqueLogins
        : null;
    const notAvailable = t('report.notAvailable');
    const row = cells => cells.map(csvField).join(',');

    const lines = [
        row([`${t('report.appName')} - ${t('report.title')}`]),
        row([
            t('report.business'),
            report.business ? report.business.name : t('report.allBusinesses')
        ]),
        row([t('report.period'), `${report.dateFrom} - ${report.dateTo}`]),
        row([t('report.generated'), formatGeneratedAt(report.generatedAt)]),
        '',
        row([t('report.metric'), t('report.value')]),
        row([
            t('summary.members'),
            totals.members === null ? notAvailable : totals.members
        ]),
        row([t('summary.uniqueLogins'), totals.uniqueLogins]),
        row([t('summary.uniqueMeditated'), totals.uniqueMeditated]),
        row([
            t('summary.meditatedRate'),
            formatRate(meditatedRate, notAvailable)
        ]),
        '',
        row([
            t('table.date'),
            t('table.uniqueLogins'),
            t('table.uniqueMeditated')
        ])
    ];

    report.daily.forEach(day => {
        lines.push(row([day.date, day.uniqueLogins, day.uniqueMeditated]));
    });

    return BOM + lines.join(EOL) + EOL;
}

/**
 * Build and download the CSV.
 * @param {object} report
 * @returns {string} the file name used
 */
export function exportCsv(report) {
    const fileName = reportFileName(report, 'csv');
    downloadText(fileName, buildCsv(report), 'text/csv');
    return fileName;
}

/**
 * Build the CSV document for the never-logged-in list: a plain two-column
 * table (full name, email), one row per member.
 * @param {object} payload API response from
 *   /business/activity-report/never-logged-in
 * @returns {string}
 */
export function buildNeverLoggedInCsv(payload) {
    const t = key => i18n.t(`activityReport.neverLoggedIn.columns.${key}`);
    const row = cells => cells.map(csvField).join(',');

    const lines = [
        row([t('fullName'), t('email')]),
        ...payload.users.map(user => row([user.fullName, user.email]))
    ];

    return BOM + lines.join(EOL) + EOL;
}

/**
 * Build and download the never-logged-in CSV.
 * @param {object} payload
 * @returns {string} the file name used
 */
export function exportNeverLoggedInCsv(payload) {
    const fileName = neverLoggedInFileName(payload, 'csv');
    downloadText(fileName, buildNeverLoggedInCsv(payload), 'text/csv');
    return fileName;
}
